import { Injectable } from '@angular/core';
import { DataService } from 'src/app/data.service';
import { Router } from '@angular/router';
import { v4 as uuidv4 } from 'uuid';
import { SubscriptionAction } from 'src/app/shared/enums/subscription';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { ConfigService } from 'src/app/shared/services/config.service';
import { AccountProfileService } from 'src/app/shared/services/evergent/account/account-profile.service';
import { EntitlementService } from 'src/app/shared/services/evergent/entitlement/entitlement.service';
import { UserEntitlementService } from 'src/app/shared/services/evergent/entitlement/user-entitlement.service';
import { LogoutService } from 'src/app/shared/services/evergent/login/logout.service';
import { MetaDataService } from 'src/app/shared/services/meta-data.service';
import { LocalStorageService } from 'src/app/shared/services/storage/local-storage.service';
import { KEYS } from 'src/app/shared/utils/constants/storage.constants';
import { getChannelPartnerIDByLocation } from 'src/app/shared/utils/evergentUtils';
import { StorefrontService } from 'src/app/storefront/storefront.service';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { SessionStorageService } from 'src/app/shared/services/storage/session-storage.service';
import { NewrelicService } from 'src/app/analytics/newrelic/services/newrelic.service';

@Injectable({
  providedIn: 'root',
})
export class SignOutService {
  constructor(
    private readonly logoutService: LogoutService,
    private readonly accountProfileService: AccountProfileService,
    private readonly localStorage: LocalStorageService,
    private readonly userEntitlementService: UserEntitlementService,
    private readonly dataService: DataService,
    private readonly configService: ConfigService,
    private readonly authService: AuthService,
    private readonly entitlementService: EntitlementService,
    private readonly metaDataService: MetaDataService,
    private readonly storefrontService: StorefrontService,
    private readonly dialogService: DialogService,
    private readonly sessionStorage: SessionStorageService,
    private readonly newrelicService: NewrelicService,
    private readonly router: Router
  ) {}

  private removeDataFromStorage(): void {
    Object.values(KEYS).forEach((key) => {
      if (
        [
          KEYS.PLATFORM_TOKEN,
          KEYS.EVERGENT_ACCESS_TOKEN,
          KEYS.EVERGENT_OVAT_TOKEN,
          KEYS.EVERGENT_OVAT_TOKEN_EXPIRY,
          KEYS.REFRESH_TOKEN_RESPONSE,
          KEYS.REFRESH_TOKEN,
          KEYS.CP_CUSTOMER_ID,
          KEYS.SELECTED_PROFILE_ID,
          KEYS.PROFILE_PREFERENCES,
          KEYS.SUBSCRIPTIONS,
          KEYS.SUBSCRIPTION_STATUS,
          KEYS.SUBSCRIPTION_PACKAGE,
          KEYS.STATUS,
          KEYS.STOREFRONT_ID,
          KEYS.TVOD_PURCHASED_LIST,
          KEYS.SELECTED_PROFILE,
          KEYS.PREFERRED_LANGUAGES,
          KEYS.EVERGENT_ACCESS_TOKEN_EXPIRY,
        ].includes(key as any)
      ) {
        this.localStorage.removeItem(key);
      }
    });
  }

  async logout() {
    if (this.dataService.isLoggingOut) {
      return;
    }
    this.dataService.isLoggingOut = true;
    try {
      await this.logoutService.logout().toPromise();
    } catch (error: any) {
      console.warn('signout error', error);
      this.newrelicService.sendErrorEvent({
        error,
      });
    } finally {
      await this.postLogoutCleanup();
    }
  }

  private updateChannelPartnerConfig(): void {
    const updatedConfig = { ...this.configService.applicationConfig };
    updatedConfig.channelPartnerID =
      getChannelPartnerIDByLocation(updatedConfig);
    this.configService.applicationConfig = updatedConfig;
  }

  private async postLogoutCleanup(): Promise<void> {
    try {
      this.updateChannelPartnerConfig();
      this.clearData();
      this.authService.resetPlayback();
      await this.authService.initPlatformService();
      await this.authService.getAccessToken();
      await this.authService.contentAuthorizer.registerDevice();
      this.sessionStorage.setItem(KEYS.SESSION_ID, uuidv4());
      this.storefrontService.initialiseServices();
      this.metaDataService.refresh();
      this.entitlementService.tvodCatalogs();
    } catch (error) {
      console.warn('post logout error', error);
      this.newrelicService.sendErrorEvent({
        error,
      });
    } finally {
      this.dataService.isLoggingOut = false;
    }
  }

  clearData() {
    this.removeDataFromStorage();
    this.accountProfileService.clean();
    this.entitlementService.resetTvodCatalog();
    this.dataService.detailResource = null;
    this.dataService.myAhaLastFocusedIndex = null;
    this.localStorage.setItem(KEYS.FRESH_LAUNCH, 'true');
    this.userEntitlementService.planActionBtn$.next(
      SubscriptionAction.SUBSCRIBE
    );
    this.resetPreferredLanguageForAnonymous();
  }

  resetPreferredLanguageForAnonymous(): void {
    const preferredLanguages = this.localStorage.getItem(
      KEYS.ANONYMOUS_PREFERRED_LANGUAGES
    );
    this.dataService.skipPreferredLanguageSelection = !!(
      preferredLanguages && preferredLanguages.length > 0
    );
  }

  forceLogout() {
    if (this.dataService.isLoggingOut) {
      return;
    }
    this.dataService.isLoggingOut = true;
    this.localStorage.setItem(KEYS.FRESH_LAUNCH, 'true');
    this.dataService.clearVerticalMenuLastEvent();
    this.postLogoutCleanup();
    this.router.navigate(['/session-timeout']);
  }
}
